/**
 * Utility for computing targeted state updates (deltas) for efficient
 * and conflict-free concurrent updates.
 */

function safeJsonEqual(a: unknown, b: unknown): boolean {
  try {
    return JSON.stringify(a) === JSON.stringify(b);
  } catch {
    // Circular reference or other serialization error — treat as different
    return false;
  }
}

export type StateDelta = {
  [path: string]: unknown;
};

export type StateDeltaResult = {
  delta: StateDelta;
  previousValues: StateDelta;
};

/**
 * Computes a flat map of paths to values that have changed between oldState and newState.
 * Uses dot notation for nested paths (e.g., "players.player1.score").
 *
 * Returns both the delta (new values) and previousValues (old values) for each changed path.
 * previousValues uses `null` for fields that didn't previously exist (MongoDB's `{ field: null }`
 * matches both null and missing fields, making this safe for CAS queries).
 *
 * @param oldState - The previous state object
 * @param newState - The new state object
 * @param prefix - Internal: path prefix for recursion
 * @returns delta (new values) and previousValues (old values) for changed paths
 */
export function computeStateDelta(
  oldState: any,
  newState: any,
  prefix: string = ''
): StateDeltaResult {
  const delta: StateDelta = {};
  const previousValues: StateDelta = {};

  // Handle null/undefined cases
  if (oldState === newState) {
    return { delta, previousValues };
  }

  if (newState === null || newState === undefined) {
    // Value was deleted — use null (survives JSON serialization, MongoDB $set handles it)
    delta[prefix || 'root'] = null;
    previousValues[prefix || 'root'] = oldState ?? null;
    return { delta, previousValues };
  }

  if (oldState === null || oldState === undefined) {
    // New value added
    delta[prefix || 'root'] = newState;
    previousValues[prefix || 'root'] = null;
    return { delta, previousValues };
  }

  // Handle primitives and non-objects
  if (typeof newState !== 'object' || typeof oldState !== 'object') {
    if (oldState !== newState) {
      delta[prefix || 'root'] = newState;
      previousValues[prefix || 'root'] = oldState;
    }
    return { delta, previousValues };
  }

  // Handle arrays — recurse into indices for granular updates.
  // Appended elements → $set with CAS check that index doesn't exist.
  // Removed trailing elements → null in delta, server uses $unset + $pull.
  if (Array.isArray(newState) || Array.isArray(oldState)) {
    if (!Array.isArray(oldState) || !Array.isArray(newState)) {
      // Type mismatch — atomic replacement
      if (!safeJsonEqual(oldState, newState)) {
        delta[prefix || 'root'] = newState;
        previousValues[prefix || 'root'] = oldState;
      }
      return { delta, previousValues };
    }

    // Both are arrays: recurse into all indices (changed, appended, and removed).
    // Unchanged elements short-circuit at the top of this function via === check.
    const maxLen = Math.max(oldState.length, newState.length);
    for (let i = 0; i < maxLen; i++) {
      const path = prefix ? `${prefix}.${i}` : `${i}`;
      const oldVal = i < oldState.length ? oldState[i] : undefined;
      const newVal = i < newState.length ? newState[i] : undefined;
      const nested = computeStateDelta(oldVal, newVal, path);
      Object.assign(delta, nested.delta);
      Object.assign(previousValues, nested.previousValues);
    }
    return { delta, previousValues };
  }

  // Handle objects - recurse into properties
  const allKeys = new Set([
    ...Object.keys(oldState),
    ...Object.keys(newState),
  ]);

  for (const key of allKeys) {
    const path = prefix ? `${prefix}.${key}` : key;
    const oldValue = oldState[key];
    const newValue = newState[key];

    // Normalize undefined: treat missing key same as key with undefined value
    const oldIsUndefined = oldValue === undefined;
    const newIsUndefined = newValue === undefined;

    // If both are undefined (missing or explicit), no change
    if (oldIsUndefined && newIsUndefined) {
      continue;
    }

    // If one is undefined and other isn't, it's a change
    if (oldIsUndefined && !newIsUndefined) {
      // Value was added — previousValue is null (field didn't exist)
      delta[path] = newValue;
      previousValues[path] = null;
    } else if (!oldIsUndefined && newIsUndefined) {
      // Value was deleted — use null instead of undefined (survives JSON serialization)
      delta[path] = null;
      previousValues[path] = oldValue;
    } else if (
      typeof newValue === 'object' &&
      newValue !== null &&
      typeof oldValue === 'object' &&
      oldValue !== null
    ) {
      // Both are objects or arrays, recurse
      const nested = computeStateDelta(oldValue, newValue, path);
      Object.assign(delta, nested.delta);
      Object.assign(previousValues, nested.previousValues);
    } else if (!safeJsonEqual(oldValue, newValue)) {
      // Primitive changed
      delta[path] = newValue;
      previousValues[path] = oldValue;
    }
  }

  return { delta, previousValues };
}

/**
 * Applies a state delta to a base state object, creating a new state object.
 *
 * @param baseState - The state to apply changes to
 * @param delta - The delta object with dot-notation paths
 * @returns New state object with delta applied
 */
export function applyStateDelta(baseState: any, delta: StateDelta): any {
  if (!delta || Object.keys(delta).length === 0) {
    return baseState;
  }

  // Deep clone the base state
  const newState = JSON.parse(JSON.stringify(baseState));

  for (const [path, value] of Object.entries(delta)) {
    if (path === 'root') {
      return value;
    }

    const parts = path.split('.');
    let current = newState;

    // Navigate to the parent of the target property
    for (let i = 0; i < parts.length - 1; i++) {
      const part = parts[i];
      if (!(part in current) || typeof current[part] !== 'object') {
        current[part] = {};
      }
      current = current[part];
    }

    // Set or delete the final property
    const lastPart = parts[parts.length - 1];
    if (value === null) {
      delete current[lastPart];
    } else {
      current[lastPart] = value;
    }
  }

  return newState;
}
